import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE_URL } from '@/config/api.config';
import axios from 'axios';

export const NetworkContext = createContext({
  showHealthPrompt: false,
  lastError: null,
  healthUrl: '',
  dismiss: () => {},
  retryLast: async () => {},
});

const isLikelyCertOrCORS = (error) => {
  if (!error) return false;
  const message = String(error?.message || '').toLowerCase();
  return (
    message.includes('failed to fetch') ||
    message.includes('networkerror') ||
    message.includes('cors') ||
    message.includes('typeerror') ||
    message.includes('net::err_cert') ||
    message.includes('ssl')
  );
};

export function NetworkGuard({ children }) {
  const [showHealthPrompt, setShowHealthPrompt] = useState(false);
  const [lastError, setLastError] = useState(null);
  const [pendingRequest, setPendingRequest] = useState(null);
  const isProduction = useMemo(() => {
    if (typeof window === 'undefined') return true;
    const host = window.location.hostname;
    return host !== 'localhost' && host !== '127.0.0.1' && host !== '::1';
  }, []);
  const healthUrl = useMemo(() => {
    try {
      const base = new URL(API_BASE_URL);
      return `${base.origin}/health`;
    } catch {
      return `${API_BASE_URL.replace(/\/$/, '')}/health`;
    }
  }, []);

  const originalFetchRef = useRef(globalThis.fetch);
  const openedThisSessionRef = useRef(false);

  const showPrompt = useCallback((error) => {
    setLastError(error);
    setShowHealthPrompt(true);
  }, []);

  const dismiss = useCallback(() => {
    setShowHealthPrompt(false);
    setLastError(null);
  }, []);

  const retryLast = useCallback(async () => {
    if (!pendingRequest) return;
    const { run, resolve, reject } = pendingRequest;
    setShowHealthPrompt(false);
    setLastError(null);
    try {
      const res = await run();
      resolve(res);
    } catch (e) {
      reject(e);
    } finally {
      setPendingRequest(null);
    }
  }, [pendingRequest]);

  useEffect(() => {
    const SES_KEY = 'network_health_opened_once';
    const attemptAutoOpenHealthOnce = () => {
      try {
        if (openedThisSessionRef.current) return;
        const alreadyOpened = sessionStorage.getItem(SES_KEY) === '1';
        if (alreadyOpened) return;
        const popup = window.open(healthUrl, '_blank', 'noopener,noreferrer');
        // Mark regardless of popup success to avoid repeated attempts
        openedThisSessionRef.current = true;
        sessionStorage.setItem(SES_KEY, '1');
        if (!popup) {
          // Popup likely blocked; fall back to redirect in same tab
          window.location.href = healthUrl;
        }
        return popup;
      } catch {
        // Ignore storage or popup errors
        return null;
      }
    };

    const wrapFetch = async (input, init) => {
      try {
        const res = await originalFetchRef.current(input, init);
        return res;
      } catch (error) {
        if (isLikelyCertOrCORS(error) && isProduction) {
          // Try to automatically open the health endpoint once per session
          attemptAutoOpenHealthOnce();
          return new Promise((resolve, reject) => {
            setPendingRequest({
              run: () => originalFetchRef.current(input, init),
              resolve,
              reject,
            });
            showPrompt(error);
          });
        }
        throw error;
      }
    };

    if (isProduction) {
      globalThis.fetch = wrapFetch;

      // Also intercept axios errors to trigger the same flow
      axios.interceptors.response.use(
        (response) => response,
        (error) => {
          if (isLikelyCertOrCORS(error) && isProduction) {
            attemptAutoOpenHealthOnce();
            return new Promise((resolve, reject) => {
              const config = error?.config;
              setPendingRequest({
                run: () => axios.request(config),
                resolve,
                reject,
              });
              showPrompt(error);
            });
          }
          return Promise.reject(error);
        }
      );
    }
    return () => {
      globalThis.fetch = originalFetchRef.current;
    };
  }, [showPrompt, healthUrl, isProduction]);

  const value = useMemo(() => ({
    showHealthPrompt,
    lastError,
    healthUrl,
    dismiss,
    retryLast,
  }), [showHealthPrompt, lastError, healthUrl, dismiss, retryLast]);

  return (
    <NetworkContext.Provider value={value}>
      {children}
    </NetworkContext.Provider>
  );
}

export default NetworkGuard;

